import { UIIcon } from '../config/icons';
import { ABFilterBar } from '../lib/ab/filter-bar';
import { ABCitySelect } from '../lib/ab/select-admin';
import { ABObjectCount } from '../lib/ab/widgets-admin';
import { UCoordinates, c_cities } from '../lib/mdl/c-city';
import { c_venues, cVenueGoogleMaps, cVenuePageURL, CVenueScene, CVenueSceneView } from '../lib/mdl/c-venue';
import { uInputTranslator } from '../lib/mdl/u-translate';
import { cVenueCanDelete } from '../lib/mdl/c-venue';
import { the_alt_token } from '../lib/me/login';
import { UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton, makeActionButton } from '../lib/ui/component/button';
import { UII18NInput } from '../lib/ui/component/i18n-input';
import { UIImageInput } from '../lib/ui/component/image';
import { UITextInput, UINumberInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UIMarkdownInput } from '../lib/ui/component/markdown';
import { UITableDefinition, UITable } from '../lib/ui/component/table';
import { uiToast } from '../lib/ui/component/toast';
import { UICheckbox } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { YArrayDataSource } from '../lib/y/data-source';
import { $form } from '../lib/y/form';
import { $location } from '../lib/y/location';
import { uiSetBG } from '../lib/ui/bg';

const map_regexp = /[^@]+@([0-9.]+),([0-9.]+),([0-9]+)(?:\.[0-9]+)?z/;

function urlToLocationZoom(url: string): [UCoordinates, number] | undefined {
    const items = map_regexp.exec(url);

    if(!items || items.length != 4) return;

    return [{ lat: parseFloat(items[1]), lng: parseFloat(items[2]) }, parseFloat(items[3])];
}

class VenueScreen extends UIScreen {
    constructor(private _venue?: CVenueSceneView) {
        super();
        this.title = _venue?.name || 'New venue';
    }

    protected override fillContent(content: UIViewSpec): void {
        const venue = this._venue;

        content.classList.add('tl-admin-page');

        const name = new UITextInput({ required: true });
        const city_id = new ABCitySelect({ required: true });
        const description = new UII18NInput(new UIMarkdownInput({ lines: 8, resizeable: true }), { translator: uInputTranslator })

        const availability = new UII18NInput(new UIMarkdownInput({ lines: 5, resizeable: true }), { translator: uInputTranslator })
        const food = new UII18NInput(new UIMarkdownInput({ lines: 3, resizeable: true }), { translator: uInputTranslator })

        const address = new UITextInput({ placeholder: 'Address', required: true });
        const location = new UITextInput({ placeholder: 'Location', required: true });
        const dont_index = new UICheckbox({ title: 'Don\'t index venue page' });
        const dont_show_in_map = new UICheckbox({ title: 'Don\'t show in map at /how' });

        const picture = new UIImageInput({ scope: 'venue' });

        const scenes_adapter = new UIInputAdapter((_s: CVenueScene, entry) => {
            const container = $el.DIV('l-input-row');
            const name = new UITextInput({ required: true });
            const capacity = new UINumberInput();
            const min_amount = new UINumberInput();
            const remove = new UIButton({ icon: UIIcon.TRASH_CAN, on_click: () => { scenes_adapter.remove(entry) } });
            container.ui_add([
                new UILabeled(name, { above: 'Name' }),
                new UILabeled(capacity, { above: 'Capacity' }),
                new UILabeled(min_amount, { above: 'Minimum amount' }),
                remove
            ]);

            return {
                view: container,
                input: $form({ id: $form.noop(), name, capacity, min_amount })
            };
        })

        const form = $form({ _: [location, dont_index, dont_show_in_map], name, city_id, description, texts: $form({ availability, food }), address, picture_ids: [picture], scenes: scenes_adapter });

        if(venue) {
            form.value = venue;
            location.value = cVenueGoogleMaps(venue);
            dont_index.value = !!venue.flags.no_index;
            dont_show_in_map.value = !!venue.flags.no_map;
        }

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(name, { above: 'Name of the venue' }),
            new UILabeled(city_id, { above: 'City' }),
            new UILabeled(description, { above: 'About the venue' }),
            new UILabeled(availability, { above: 'Availability' }),
            new UILabeled(food, { above: 'Food & drink info' }),
            new UILabeled(address, { above: 'Address' }),
            new UILabeled(location, { above: 'Map location' }),
            dont_index,
            dont_show_in_map,
            new UILabeled(picture, { above: 'Picture' }),
            $el.H3('heading', 'Scenes'),
            new UIList(scenes_adapter, { classes: 'l-form' }),
            new UIButton({ title: '+ Add scene', kind: 'k-text', on_click: () => { scenes_adapter.push({} as CVenueScene, true) } })
        ]);

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;
                const value = form.value;

                const location_result = urlToLocationZoom(location.value);
                if(!location_result) {
                    location.markInvalid(true);
                    return;
                }
                value.location = location_result[0];
                value.map_zoom = location_result[1];
                value.flags = {
                    no_index: dont_index.value ? true : undefined,
                    no_map: dont_show_in_map.value ? true : undefined
                }

                c_venues.upsert(venue?.id, value).then(() => {
                    $location.pop();
                }, uiToast);
            }
        });

        addTooltip(venue ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        content.ui_add([section, button]);
    }
}

export class AdminVenuesScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const doSearch = () => {
            let venues = c_venues.all;

            for(const f of filters.filters) {
                switch(f.key) {
                    case 'city':
                        venues = venues.filter(v => f.value.includes(v.city_id));
                        break;
                }
            }

            venues = filter_bar.search_input!.filterArray(venues, (v, w) => {
                if(v.name.toLowerCase().includes(w)) return true;
                if(c_cities.maybe(v.city_id)?.name.toLowerCase().includes(w)) return true;
                return false;
            });

            count.number = venues.length;
            source.data = venues;
        }

        const excel_download = new UIButton({ kind: 'k-filter', icon: UIIcon.DOWNLOAD, title: 'Excel', link: `/docs/venues/addresses?token=${the_alt_token}` });
        const count = new ABObjectCount('venues');

        const filter_bar = new ABFilterBar({
            on_search: doSearch,
            on_filter: doSearch,
            right_content: [excel_download, count],
            no_header: true
        });

        const filters = filter_bar.filters;
        filters.addFilterButton();
        filters.addCity();

        const def = new UITableDefinition<CVenueSceneView>();
        def.addCustom({
            width: -50, classes: 'tl-thumb-cell', decorator: (td, row) => {
                const model = row.item;
                const image = $el.DIV('r-image');
                uiSetBG(image, { media: model.picture_ids[0], variant: 'size-500' });
                td.appendChild(image);
            }
        });

        def.addValue(v => v.name, { title: 'Name', width: 200 });
        def.addValue(v => c_cities.maybe(v.city_id)!.name, { title: 'City', width: -120 });

        def.addButtons([
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Delete', when: cVenueCanDelete, action: v => {
                    return showWarning({ text: "Are you sure you want to delete this venue?" }).then(() => {
                        return c_venues.remove(v.id).then(doSearch);
                    });
                }
            },
            {
                icon: UIIcon.PENCIL, tooltip: 'Edit', action: v => {
                    $location.push({ transient: new VenueScreen(v) });
                }
            },
            { icon: UIIcon.EYE, tooltip: 'View', link: cVenuePageURL }
        ], { width: -125 });

        def.preference_prefix = 'venues';

        const source = new YArrayDataSource<CVenueSceneView>();
        const table = new UITable(def, source);

        content.ui_add([
            $el.SECTION('pad-t-narrow', $el.DIV('tl-new-list-block m-narrow', [
                filter_bar,
                table,
            ])),
            makeActionButton(UIIcon.PLUS, 'New venue', () => { $location.push({ transient: new VenueScreen }) })
        ]);

        doSearch();
    }
}